import { normalizeProviderId } from "./provider-id.js";

export type InferenceProviderId =
  | "anthropic"
  | "openai"
  | "google"
  | "azure-openai"
  | "together-ai"
  | "replicate";

export type InferenceProviderFieldKind = "api_key" | "url";

export type ProxyProviderAuthApiKeyField =
  | "openaiApiKey"
  | "anthropicApiKey"
  | "googleApiKey"
  | "azureOpenaiApiKey";

export type ProxyProviderAuthExtraField = "azureOpenaiEndpoint";
export type ProxyProviderAuthField = ProxyProviderAuthApiKeyField | ProxyProviderAuthExtraField;
export type ProxyProviderAuthPayload = Partial<Record<ProxyProviderAuthField, string>>;

export type InferenceModelTier =
  | "tier_reasoning"
  | "tier_workhorse"
  | "tier_fast"
  | "tier_local_small"
  | "tier_local_medium";

export type InferenceComplexity = "routine" | "standard" | "complex";
export type InferenceProviderApi =
  | "anthropic-messages"
  | "openai-completions"
  | "google-generative-ai";
export type InferenceProviderInputMode = "text" | "image";

export interface InferenceProviderFieldDefinition {
  id: string;
  label: string;
  kind: InferenceProviderFieldKind;
  placeholder: string;
  helper: string;
  required: boolean;
  secret?: boolean;
  envVars?: string[];
}

export interface InferenceProviderModelDefinition {
  id: string;
  name: string;
  provider: InferenceProviderId;
  enabled: boolean;
  tier: InferenceModelTier;
  costPerInputToken: number;
  costPerOutputToken: number;
  costPerCacheReadToken?: number;
  costPerCacheWriteToken?: number;
  api?: InferenceProviderApi;
  contextWindow?: number;
  maxTokens?: number;
  reasoning?: boolean;
  input?: InferenceProviderInputMode[];
  aliases?: string[];
  routingComplexities?: InferenceComplexity[];
}

export interface ManagedRuntimeModelCatalogEntry {
  id: string;
  name: string;
  provider: InferenceProviderId;
  api: InferenceProviderApi;
  contextWindow: number;
  maxTokens: number;
  costPerInputToken: number;
  costPerOutputToken: number;
  costPerCacheReadToken: number;
  costPerCacheWriteToken: number;
  reasoning: boolean;
  input: InferenceProviderInputMode[];
}

export interface ManagedInferenceModelSummary {
  id: string;
  name: string;
  provider: InferenceProviderId;
  enabled: boolean;
  costPerInputToken: number;
  costPerOutputToken: number;
}

export interface InferenceCostRateDefinition {
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
}

export interface ProviderComplexityCatalogEntry {
  provider: InferenceProviderId;
  complexity: InferenceComplexity;
  model: InferenceProviderModelDefinition;
}

export interface InferenceProviderDefinition {
  id: InferenceProviderId;
  label: string;
  description: string;
  fields: InferenceProviderFieldDefinition[];
  starterApi?: {
    visible: boolean;
    order: number;
    description: string;
    helper: string;
  };
  proxyAuth?: {
    apiKeyField: ProxyProviderAuthApiKeyField;
    extraFields?: Partial<Record<string, ProxyProviderAuthExtraField>>;
  };
  managedDefaultModel?: string;
  provisioningDefaultModel?: string;
  models?: InferenceProviderModelDefinition[];
  aliases?: string[];
}

const INFERENCE_PROVIDER_REGISTRY: readonly InferenceProviderDefinition[] = [
  {
    id: "openai",
    label: "OpenAI",
    description: "GPT models, DALL-E, Whisper, embeddings",
    fields: [
      {
        id: "key",
        label: "API key",
        kind: "api_key",
        placeholder: "sk-...",
        helper: "Paste your OpenAI API key here.",
        required: true,
        secret: true,
        envVars: ["OPENAI_API_KEY"],
      },
    ],
    starterApi: {
      visible: true,
      order: 1,
      description: "Use your OpenAI API key.",
      helper: "Paste your OpenAI API key here.",
    },
    proxyAuth: {
      apiKeyField: "openaiApiKey",
    },
    managedDefaultModel: "gpt-4o",
    provisioningDefaultModel: "gpt-4o-mini",
    models: [
      {
        id: "gpt-4o-mini",
        name: "GPT-4o Mini",
        provider: "openai",
        enabled: true,
        tier: "tier_fast",
        costPerInputToken: 0.00000015,
        costPerOutputToken: 0.0000006,
        costPerCacheReadToken: 0.000000075,
        costPerCacheWriteToken: 0,
        api: "openai-completions",
        contextWindow: 128_000,
        maxTokens: 16_384,
        reasoning: false,
        input: ["text", "image"],
        aliases: ["gpt-mini", "4o-mini"],
      },
      {
        id: "gpt-4o",
        name: "GPT-4o",
        provider: "openai",
        enabled: true,
        tier: "tier_workhorse",
        costPerInputToken: 0.0000025,
        costPerOutputToken: 0.00001,
        costPerCacheReadToken: 0.00000125,
        costPerCacheWriteToken: 0,
        api: "openai-completions",
        contextWindow: 128_000,
        maxTokens: 16_384,
        reasoning: false,
        input: ["text", "image"],
        aliases: ["4o"],
      },
      {
        id: "o3",
        name: "o3",
        provider: "openai",
        enabled: true,
        tier: "tier_reasoning",
        costPerInputToken: 0.00001,
        costPerOutputToken: 0.00004,
        costPerCacheReadToken: 0.000005,
        costPerCacheWriteToken: 0,
        api: "openai-completions",
        contextWindow: 200_000,
        maxTokens: 100_000,
        reasoning: true,
        input: ["text", "image"],
      },
    ],
  },
  {
    id: "anthropic",
    label: "Claude",
    description: "Claude models via Anthropic API",
    fields: [
      {
        id: "key",
        label: "API key",
        kind: "api_key",
        placeholder: "sk-ant-...",
        helper: "Paste your Anthropic API key here.",
        required: true,
        secret: true,
        envVars: ["ANTHROPIC_API_KEY"],
      },
    ],
    starterApi: {
      visible: true,
      order: 2,
      description: "Use your Anthropic API key.",
      helper: "Paste your Anthropic API key here.",
    },
    proxyAuth: {
      apiKeyField: "anthropicApiKey",
    },
    managedDefaultModel: "claude-sonnet-4-6",
    provisioningDefaultModel: "claude-haiku-4-5",
    models: [
      {
        id: "claude-haiku-4-5-20251001",
        name: "Claude Haiku 4.5",
        provider: "anthropic",
        enabled: true,
        tier: "tier_fast",
        costPerInputToken: 0.000001,
        costPerOutputToken: 0.000005,
        costPerCacheReadToken: 0.0000001,
        costPerCacheWriteToken: 0.00000125,
        api: "anthropic-messages",
        contextWindow: 200_000,
        maxTokens: 8_192,
        reasoning: false,
        input: ["text", "image"],
        aliases: ["haiku", "claude-haiku", "claude-haiku-4-5", "claude-haiku-4-5-20251001"],
      },
      {
        id: "claude-sonnet-4-6",
        name: "Claude Sonnet 4.6",
        provider: "anthropic",
        enabled: true,
        tier: "tier_workhorse",
        costPerInputToken: 0.000003,
        costPerOutputToken: 0.000015,
        costPerCacheReadToken: 0.0000003,
        costPerCacheWriteToken: 0.00000375,
        api: "anthropic-messages",
        contextWindow: 200_000,
        maxTokens: 16_384,
        reasoning: true,
        input: ["text", "image"],
        aliases: ["sonnet", "claude-sonnet"],
      },
      {
        id: "claude-opus-4-6",
        name: "Claude Opus 4.6",
        provider: "anthropic",
        enabled: true,
        tier: "tier_reasoning",
        costPerInputToken: 0.000015,
        costPerOutputToken: 0.000075,
        costPerCacheReadToken: 0.0000015,
        costPerCacheWriteToken: 0.00001875,
        api: "anthropic-messages",
        contextWindow: 200_000,
        maxTokens: 32_768,
        reasoning: true,
        input: ["text", "image"],
        aliases: ["opus", "claude-opus"],
      },
    ],
  },
  {
    id: "google",
    label: "Gemini",
    description: "Gemini models via Google AI Studio or Gemini API",
    fields: [
      {
        id: "key",
        label: "API key",
        kind: "api_key",
        placeholder: "AIza...",
        helper: "Paste your Google AI Studio or Gemini API key here.",
        required: true,
        secret: true,
        envVars: ["GEMINI_API_KEY", "GOOGLE_API_KEY"],
      },
    ],
    starterApi: {
      visible: true,
      order: 3,
      description: "Use your Google AI Studio or Gemini API key.",
      helper: "Paste your Google AI Studio or Gemini API key here.",
    },
    proxyAuth: {
      apiKeyField: "googleApiKey",
    },
    managedDefaultModel: "gemini-2.5-pro",
    provisioningDefaultModel: "gemini-2.5-pro",
    aliases: ["gemini", "vertex"],
    models: [
      {
        id: "gemini-2.0-flash",
        name: "Gemini 2.0 Flash",
        provider: "google",
        enabled: true,
        tier: "tier_fast",
        costPerInputToken: 0.0000001,
        costPerOutputToken: 0.0000004,
        costPerCacheReadToken: 0,
        costPerCacheWriteToken: 0,
        api: "google-generative-ai",
        contextWindow: 1_000_000,
        maxTokens: 8_192,
        reasoning: false,
        input: ["text", "image"],
        aliases: ["flash", "gemini-flash"],
      },
      {
        id: "gemini-2.5-pro",
        name: "Gemini 2.5 Pro",
        provider: "google",
        enabled: true,
        tier: "tier_workhorse",
        costPerInputToken: 0.00000125,
        costPerOutputToken: 0.00001,
        costPerCacheReadToken: 0,
        costPerCacheWriteToken: 0,
        api: "google-generative-ai",
        contextWindow: 1_000_000,
        maxTokens: 65_536,
        reasoning: true,
        input: ["text", "image"],
        aliases: ["gemini-pro"],
      },
      {
        id: "gemini-2.5-pro-preview",
        name: "Gemini 2.5 Pro Preview",
        provider: "google",
        enabled: true,
        tier: "tier_reasoning",
        costPerInputToken: 0.00000125,
        costPerOutputToken: 0.00001,
        costPerCacheReadToken: 0,
        costPerCacheWriteToken: 0,
        api: "google-generative-ai",
        contextWindow: 1_000_000,
        maxTokens: 65_536,
        reasoning: true,
        input: ["text", "image"],
      },
    ],
  },
  {
    id: "azure-openai",
    label: "Azure OpenAI",
    description: "GPT models through your Azure OpenAI deployment",
    fields: [
      {
        id: "key",
        label: "API key",
        kind: "api_key",
        placeholder: "Paste Azure OpenAI API key",
        helper: "Paste your Azure OpenAI API key here.",
        required: true,
        secret: true,
        envVars: ["AZURE_OPENAI_API_KEY"],
      },
      {
        id: "endpoint",
        label: "Endpoint",
        kind: "url",
        placeholder: "https://<resource>.cognitiveservices.azure.com/",
        helper: "Paste the Azure OpenAI resource endpoint for this deployment.",
        required: true,
        envVars: ["AZURE_OPENAI_ENDPOINT"],
      },
    ],
    starterApi: {
      visible: true,
      order: 4,
      description: "Use your Azure OpenAI key and endpoint.",
      helper: "Paste your Azure OpenAI API key and resource endpoint here.",
    },
    proxyAuth: {
      apiKeyField: "azureOpenaiApiKey",
      extraFields: {
        endpoint: "azureOpenaiEndpoint",
      },
    },
    managedDefaultModel: "gpt-5.4-mini",
    provisioningDefaultModel: "gpt-5.4-nano",
    aliases: ["azure"],
    models: [
      {
        id: "gpt-5.4-nano",
        name: "GPT-5.4 Nano (Azure)",
        provider: "azure-openai",
        enabled: true,
        tier: "tier_fast",
        costPerInputToken: 0.00000015,
        costPerOutputToken: 0.0000006,
        costPerCacheReadToken: 0.000000075,
        costPerCacheWriteToken: 0,
        api: "openai-completions",
        contextWindow: 128_000,
        maxTokens: 16_384,
        reasoning: false,
        input: ["text", "image"],
        routingComplexities: ["routine"],
      },
      {
        id: "gpt-5.4-mini",
        name: "GPT-5.4 Mini (Azure)",
        provider: "azure-openai",
        enabled: true,
        tier: "tier_workhorse",
        costPerInputToken: 0.0000003,
        costPerOutputToken: 0.0000012,
        costPerCacheReadToken: 0.00000015,
        costPerCacheWriteToken: 0,
        api: "openai-completions",
        contextWindow: 128_000,
        maxTokens: 16_384,
        reasoning: false,
        input: ["text", "image"],
        routingComplexities: ["standard", "complex"],
      },
      {
        id: "gpt-5.4",
        name: "GPT-5.4 (Azure)",
        provider: "azure-openai",
        enabled: true,
        tier: "tier_reasoning",
        costPerInputToken: 0.00001,
        costPerOutputToken: 0.00004,
        costPerCacheReadToken: 0.000005,
        costPerCacheWriteToken: 0,
        api: "openai-completions",
        contextWindow: 200_000,
        maxTokens: 32_768,
        reasoning: true,
        input: ["text", "image"],
      },
    ],
  },
  {
    id: "together-ai",
    label: "Together AI",
    description: "Open-source model inference (Llama, Mistral)",
    aliases: ["together"],
    fields: [
      {
        id: "key",
        label: "API key",
        kind: "api_key",
        placeholder: "Paste Together AI API key",
        helper: "Paste your Together AI API key here.",
        required: true,
        secret: true,
        envVars: ["TOGETHER_API_KEY"],
      },
    ],
  },
  {
    id: "replicate",
    label: "Replicate",
    description: "Run any model via API (image, audio, video)",
    fields: [
      {
        id: "key",
        label: "API token",
        kind: "api_key",
        placeholder: "Paste Replicate API token",
        helper: "Paste your Replicate API token here.",
        required: true,
        secret: true,
        envVars: ["REPLICATE_API_TOKEN"],
      },
    ],
  },
] as const;

const PROVIDER_MAP = new Map(
  INFERENCE_PROVIDER_REGISTRY.map((provider) => [provider.id, provider]),
);

const MODEL_LOOKUP = new Map<
  string,
  { provider: InferenceProviderDefinition; model: InferenceProviderModelDefinition }
>();

for (const provider of INFERENCE_PROVIDER_REGISTRY) {
  for (const model of provider.models ?? []) {
    MODEL_LOOKUP.set(model.id.toLowerCase(), { provider, model });
    for (const alias of model.aliases ?? []) {
      MODEL_LOOKUP.set(alias.toLowerCase(), { provider, model });
    }
  }
}

const COMPLEXITY_TIER_PREFERENCE: Record<InferenceComplexity, readonly InferenceModelTier[]> = {
  routine: ["tier_fast", "tier_workhorse", "tier_reasoning"],
  standard: ["tier_workhorse", "tier_reasoning", "tier_fast"],
  complex: ["tier_reasoning", "tier_workhorse", "tier_fast"],
};

function cloneField(field: InferenceProviderFieldDefinition): InferenceProviderFieldDefinition {
  return {
    ...field,
    ...(field.envVars ? { envVars: [...field.envVars] } : {}),
  };
}

function cloneModel(model: InferenceProviderModelDefinition): InferenceProviderModelDefinition {
  return {
    ...model,
    ...(model.input ? { input: [...model.input] } : {}),
    ...(model.aliases ? { aliases: [...model.aliases] } : {}),
    ...(model.routingComplexities ? { routingComplexities: [...model.routingComplexities] } : {}),
  };
}

function cloneProvider(provider: InferenceProviderDefinition): InferenceProviderDefinition {
  return {
    ...provider,
    fields: provider.fields.map(cloneField),
    ...(provider.starterApi ? { starterApi: { ...provider.starterApi } } : {}),
    ...(provider.proxyAuth
      ? {
          proxyAuth: {
            ...provider.proxyAuth,
            ...(provider.proxyAuth.extraFields
              ? { extraFields: { ...provider.proxyAuth.extraFields } }
              : {}),
          },
        }
      : {}),
    ...(provider.models ? { models: provider.models.map(cloneModel) } : {}),
    ...(provider.aliases ? { aliases: [...provider.aliases] } : {}),
  };
}

export function normalizeInferenceProviderId(
  provider: string | undefined,
): InferenceProviderId | undefined {
  const normalized = String(provider || "").trim();
  if (!normalized) return undefined;
  const canonical = normalizeProviderId(normalized);
  if (PROVIDER_MAP.has(canonical as InferenceProviderId)) {
    return canonical as InferenceProviderId;
  }
  for (const entry of INFERENCE_PROVIDER_REGISTRY) {
    if (entry.aliases?.includes(canonical)) {
      return entry.id;
    }
  }
  return undefined;
}

export function getInferenceProvider(
  provider: string | undefined,
): InferenceProviderDefinition | undefined {
  const normalized = normalizeInferenceProviderId(provider);
  return normalized ? cloneProvider(PROVIDER_MAP.get(normalized)!) : undefined;
}

export function listInferenceProviders(): InferenceProviderDefinition[] {
  return INFERENCE_PROVIDER_REGISTRY.map(cloneProvider);
}

export function listStarterApiInferenceProviders(): InferenceProviderDefinition[] {
  return INFERENCE_PROVIDER_REGISTRY.filter((provider) => provider.starterApi?.visible)
    .sort(
      (left, right) =>
        (left.starterApi?.order ?? Number.MAX_SAFE_INTEGER) -
        (right.starterApi?.order ?? Number.MAX_SAFE_INTEGER),
    )
    .map(cloneProvider);
}

export function listManagedInferenceProviders(): InferenceProviderDefinition[] {
  return INFERENCE_PROVIDER_REGISTRY.filter(
    (provider) => provider.managedDefaultModel && provider.models?.length,
  ).map(cloneProvider);
}

export function buildManagedProviderDefaults(): Record<string, string> {
  return Object.fromEntries(
    INFERENCE_PROVIDER_REGISTRY.flatMap((provider) =>
      provider.managedDefaultModel ? [[provider.id, provider.managedDefaultModel] as const] : [],
    ),
  );
}

export function resolveConfiguredDefaultManagedProvider(
  source: Record<string, string | undefined> = process.env,
): InferenceProviderDefinition | undefined {
  const preferred = normalizeInferenceProviderId(source.INFERENCE_DEFAULT_PROVIDER);
  if (!preferred) return undefined;
  const provider = PROVIDER_MAP.get(preferred);
  if (!provider?.managedDefaultModel || !provider.models?.length) return undefined;
  return cloneProvider(provider);
}

export function resolveManagedProviderDefaultModel(
  provider: string | undefined,
): string | undefined {
  return getInferenceProvider(provider)?.managedDefaultModel;
}

export function resolveFirstConfiguredManagedModel(
  source: Record<string, string | undefined> = process.env,
): string | undefined {
  const preferred = resolveConfiguredDefaultManagedProvider(source);
  if (preferred) {
    return hasConfiguredInferenceProvider(preferred.id, source)
      ? preferred.managedDefaultModel
      : undefined;
  }
  for (const provider of INFERENCE_PROVIDER_REGISTRY) {
    if (!provider.managedDefaultModel) continue;
    if (!hasConfiguredInferenceProvider(provider.id, source)) continue;
    return provider.managedDefaultModel;
  }
  return undefined;
}

export function buildProvisioningProviderDefaults(): Record<string, string> {
  return Object.fromEntries(
    INFERENCE_PROVIDER_REGISTRY.flatMap((provider) =>
      provider.provisioningDefaultModel
        ? [[provider.id, provider.provisioningDefaultModel] as const]
        : [],
    ),
  );
}

export function getRequiredInferenceEnvKeys(): string[] {
  return Array.from(
    new Set(
      INFERENCE_PROVIDER_REGISTRY.flatMap((provider) =>
        provider.fields.flatMap((field) => field.envVars ?? []),
      ),
    ),
  );
}

export function readProviderFieldFromEnv(
  provider: string | undefined,
  fieldId: string,
  source: Record<string, string | undefined> = process.env,
): string | undefined {
  const definition = getInferenceProvider(provider);
  const field = definition?.fields.find((candidate) => candidate.id === fieldId);
  if (!field?.envVars?.length) return undefined;
  for (const envVar of field.envVars) {
    const value = source[envVar]?.trim();
    if (value) return value;
  }
  return undefined;
}

export function hasConfiguredInferenceProvider(
  provider: string | undefined,
  source: Record<string, string | undefined> = process.env,
): boolean {
  const definition = getInferenceProvider(provider);
  if (!definition) return false;
  return definition.fields.every((field) => {
    if (!field.required) return true;
    return Boolean(readProviderFieldFromEnv(definition.id, field.id, source));
  });
}

export function getInferenceProviderModels(
  provider: string | undefined,
): InferenceProviderModelDefinition[] {
  return getInferenceProvider(provider)?.models?.map(cloneModel) ?? [];
}

export function findInferenceProviderModel(
  modelId: string | undefined,
): { provider: InferenceProviderDefinition; model: InferenceProviderModelDefinition } | null {
  const normalized = String(modelId || "")
    .trim()
    .toLowerCase();
  if (!normalized) return null;
  const match = MODEL_LOOKUP.get(normalized);
  if (!match) return null;
  return {
    provider: cloneProvider(match.provider),
    model: cloneModel(match.model),
  };
}

function defaultCacheReadRate(model: InferenceProviderModelDefinition): number {
  if (typeof model.costPerCacheReadToken === "number") {
    return model.costPerCacheReadToken;
  }
  return model.provider === "anthropic"
    ? model.costPerInputToken * 0.1
    : model.costPerInputToken * 0.5;
}

function defaultCacheWriteRate(model: InferenceProviderModelDefinition): number {
  if (typeof model.costPerCacheWriteToken === "number") {
    return model.costPerCacheWriteToken;
  }
  return model.provider === "anthropic" ? model.costPerInputToken * 1.25 : 0;
}

function defaultApi(provider: InferenceProviderId): InferenceProviderApi {
  if (provider === "anthropic") return "anthropic-messages";
  if (provider === "google") return "google-generative-ai";
  return "openai-completions";
}

function defaultInput(): InferenceProviderInputMode[] {
  return ["text", "image"];
}

export function buildManagedRuntimeModelCatalog(): ManagedRuntimeModelCatalogEntry[] {
  return INFERENCE_PROVIDER_REGISTRY.flatMap((provider) =>
    (provider.models ?? [])
      .filter((model) => model.enabled)
      .map((model) => ({
        id: model.id,
        name: model.name,
        provider: model.provider,
        api: model.api ?? defaultApi(provider.id),
        contextWindow: model.contextWindow ?? 128_000,
        maxTokens: model.maxTokens ?? 4_096,
        costPerInputToken: model.costPerInputToken,
        costPerOutputToken: model.costPerOutputToken,
        costPerCacheReadToken: defaultCacheReadRate(model),
        costPerCacheWriteToken: defaultCacheWriteRate(model),
        reasoning: model.reasoning ?? model.tier === "tier_reasoning",
        input: model.input ? [...model.input] : defaultInput(),
      })),
  );
}

export function buildManagedModelSummaries(): ManagedInferenceModelSummary[] {
  return INFERENCE_PROVIDER_REGISTRY.flatMap((provider) =>
    (provider.models ?? [])
      .filter((model) => model.enabled)
      .map((model) => ({
        id: model.id,
        name: model.name,
        provider: model.provider,
        enabled: model.enabled,
        costPerInputToken: model.costPerInputToken,
        costPerOutputToken: model.costPerOutputToken,
      })),
  );
}

export function buildManagedModelAliasMap(): Record<string, string> {
  return Object.fromEntries(
    INFERENCE_PROVIDER_REGISTRY.flatMap((provider) =>
      (provider.models ?? []).flatMap((model) =>
        (model.aliases ?? []).map((alias) => [alias, model.id] as const),
      ),
    ),
  );
}

export function buildManagedInferenceCostTable(): Record<string, InferenceCostRateDefinition> {
  return Object.fromEntries(
    INFERENCE_PROVIDER_REGISTRY.flatMap((provider) =>
      (provider.models ?? []).flatMap((model) => {
        const ids = [model.id, ...(model.aliases ?? [])].map(
          (id) => `${provider.id}/${String(id).trim().toLowerCase()}`,
        );
        const value: InferenceCostRateDefinition = {
          input: model.costPerInputToken,
          output: model.costPerOutputToken,
          cacheRead: defaultCacheReadRate(model),
          cacheWrite: defaultCacheWriteRate(model),
        };
        return ids.map((id) => [id, value] as const);
      }),
    ),
  );
}

function normalizeProxyProviderAuthValue(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function getProxyProviderAuthDefinition(provider: string | undefined):
  | {
      provider: InferenceProviderDefinition;
      apiKeyField: ProxyProviderAuthApiKeyField;
      extraFields: Partial<Record<string, ProxyProviderAuthExtraField>>;
    }
  | undefined {
  const definition = getInferenceProvider(provider);
  if (!definition?.proxyAuth?.apiKeyField) {
    return undefined;
  }
  return {
    provider: definition,
    apiKeyField: definition.proxyAuth.apiKeyField,
    extraFields: definition.proxyAuth.extraFields ?? {},
  };
}

export function readProxyProviderAuthApiKey(
  providerAuth: ProxyProviderAuthPayload | undefined,
  provider: string | undefined,
): string | undefined {
  const definition = getProxyProviderAuthDefinition(provider);
  if (!definition) return undefined;
  return normalizeProxyProviderAuthValue(providerAuth?.[definition.apiKeyField]);
}

export function readProxyProviderAuthMetadata(
  providerAuth: ProxyProviderAuthPayload | undefined,
  provider: string | undefined,
): Record<string, string> {
  const definition = getProxyProviderAuthDefinition(provider);
  if (!definition) return {};

  const metadata: Record<string, string> = {};
  for (const [fieldId, proxyField] of Object.entries(definition.extraFields)) {
    if (!proxyField) continue;
    const value = normalizeProxyProviderAuthValue(providerAuth?.[proxyField]);
    if (value) {
      metadata[fieldId] = value;
    }
  }
  return metadata;
}

export function hasCompleteProxyProviderAuth(
  providerAuth: ProxyProviderAuthPayload | undefined,
  provider: string | undefined,
): boolean {
  const definition = getProxyProviderAuthDefinition(provider);
  if (!definition) return false;
  if (!readProxyProviderAuthApiKey(providerAuth, definition.provider.id)) {
    return false;
  }

  for (const field of definition.provider.fields) {
    if (field.id === "key" || !field.required) continue;
    const proxyField = definition.extraFields[field.id];
    if (!proxyField) {
      return false;
    }
    if (!normalizeProxyProviderAuthValue(providerAuth?.[proxyField])) {
      return false;
    }
  }
  return true;
}

export function listConfiguredProxyProviderAuthProviders(
  providerAuth: ProxyProviderAuthPayload | undefined,
): InferenceProviderDefinition[] {
  return listInferenceProviders().filter((provider) =>
    hasCompleteProxyProviderAuth(providerAuth, provider.id),
  );
}

export function mergeProviderCredentialIntoProxyAuth(params: {
  base?: ProxyProviderAuthPayload;
  provider?: string;
  apiKey?: string;
  metadata?: Record<string, string | undefined>;
  source?: Record<string, string | undefined>;
}): ProxyProviderAuthPayload | undefined {
  const next: ProxyProviderAuthPayload = params.base ? { ...params.base } : {};
  const definition = getProxyProviderAuthDefinition(params.provider);
  const apiKey = normalizeProxyProviderAuthValue(params.apiKey);
  if (definition && apiKey) {
    next[definition.apiKeyField] ??= apiKey;
    for (const field of definition.provider.fields) {
      if (field.id === "key") continue;
      const proxyField = definition.extraFields[field.id];
      if (!proxyField) continue;
      const value =
        normalizeProxyProviderAuthValue(params.metadata?.[field.id]) ??
        readProviderFieldFromEnv(definition.provider.id, field.id, params.source);
      if (value) {
        next[proxyField] ??= value;
      }
    }
  }

  return Object.keys(next).length > 0 ? next : undefined;
}

export function buildProxyProviderAuthSourceMap(
  providerAuth: ProxyProviderAuthPayload | undefined,
  source = "auth-profile",
): Record<string, string> {
  return Object.fromEntries(
    listConfiguredProxyProviderAuthProviders(providerAuth).map((provider) => [provider.id, source]),
  );
}

function selectModelForComplexity(
  provider: InferenceProviderDefinition,
  complexity: InferenceComplexity,
): InferenceProviderModelDefinition | undefined {
  const models = provider.models?.filter((model) => model.enabled) ?? [];
  const explicit = models.find((model) => model.routingComplexities?.includes(complexity));
  if (explicit) return explicit;
  for (const tier of COMPLEXITY_TIER_PREFERENCE[complexity]) {
    const tierMatch = models.find((model) => model.tier === tier);
    if (tierMatch) return tierMatch;
  }
  return undefined;
}

export function buildProviderComplexityCatalog(): Record<
  string,
  Record<InferenceComplexity, ProviderComplexityCatalogEntry>
> {
  const catalog: Record<string, Record<InferenceComplexity, ProviderComplexityCatalogEntry>> = {};

  for (const provider of INFERENCE_PROVIDER_REGISTRY) {
    const routine = selectModelForComplexity(provider, "routine");
    const standard = selectModelForComplexity(provider, "standard");
    const complex = selectModelForComplexity(provider, "complex");
    if (!routine || !standard || !complex) continue;

    catalog[provider.id] = {
      routine: {
        provider: provider.id,
        complexity: "routine",
        model: cloneModel(routine),
      },
      standard: {
        provider: provider.id,
        complexity: "standard",
        model: cloneModel(standard),
      },
      complex: {
        provider: provider.id,
        complexity: "complex",
        model: cloneModel(complex),
      },
    };
  }

  return catalog;
}
